import type { InputRouter, LayerHandle, LayerKeyAnswer, RoutedKey } from "../../ui/input/router.ts";
import { ABILITY_ACTIONS, abilityAction, type ActionId } from "../controls/actions.ts";
import { codeLabel, type KeyLayout } from "../controls/layout.ts";
import { pageKeys, type KeyTable } from "../controls/page.ts";
import { actionPress } from "../controls/press.ts";
import { wheelDelta } from "./rules.ts";
import type { EmoteWheelStore } from "./store.ts";

/**
 * The emote wheel's keys and its mouse wheel (P2.19-T8; ADR-114 decisions 2.1 to 2.4 and its note of 2026-10-08),
 * named here by their defaults.
 *
 * H is the key the page's key table binds Emotes to (`controls/`), read on each press and as the wheel's layer
 * opens. It is a game key: it opens the wheel only while the game owns the keyboard, so never while a text field has
 * focus or a modal holds the keys, and only while no layer that names a key is open (the reading panel, the flag
 * panel), so one panel never opens over another; a play window's layer names no key and does not count. Whether the
 * wheel may open at all (the gateway has offered emotes, the character is in the world, alive and not frozen) is the
 * store's to say. H again closes it.
 *
 * While the wheel is open it is a layer of the input router that names the wheel's own keys and holds nothing: Q and
 * E turn the page, Enter sends the selection, H closes, and Escape closes it as it closes any topmost layer. Every
 * other key reaches the game as though nothing were open, so the character walks, fights and targets with the wheel
 * open. Q, E and Enter are the layer's own and follow no binding, with one exception that keeps decision 2.4's
 * promise ("it keeps every fighting key"): a page key that the key table has given to a fighting action, an ability,
 * the stance or Target nearest, is not named, so that action still answers it; the page then turns by the other key,
 * the header's control and the dots. Enter on a focused control keeps the control's own meaning.
 *
 * Shift changes nothing: with it held, as a player who runs holds it, H opens and closes the wheel, Q and E turn the
 * page and Enter sends, exactly as without it. A press with Ctrl, Alt or Meta held is the browser's and is declined.
 *
 * The router answers a layer's keys only while the layer is topmost and no text field has focus, so with the chat
 * field focused every key types and the wheel waits, and with a play window opened over the wheel its keys are the
 * game's. `EmoteKeys.live` says which of the three keys the wheel may show as live, so its glyphs never claim a key
 * the layer does not hold.
 *
 * The mouse wheel turns the page while the pointer is over the wheel or over the world, and nowhere else: over
 * the chat log or a window the event is that surface's. The listener is not passive and prevents the default only
 * where it acts.
 */

/**
 * The key that turns to the page before, the key that turns to the page after and the key that sends, by physical key
 * (`KeyboardEvent.code`), the wheel's own and no action's (ADR-061's amendment for P1.9-T174, point 9): Q's and E's
 * places and Enter on every layout.
 */
export const EMOTE_LAYER_KEYS = { before: "KeyQ", after: "KeyE", send: "Enter" } as const;

/**
 * The three keys as the wheel's glyphs show them, each the label of its physical key by a layout (P1.9-T174, point 8),
 * so a legend cannot disagree with the key the layer names and reads what the player's keyboard prints there.
 *
 * @param layout The layout keys are named by: the page table's, as the Keyboard layout choice sets it.
 * @returns The page before's, the page after's and the send key's labels.
 * @example
 * emoteKeyLabels(PRESET_LAYOUTS.azerty).before;
 * // "A"
 */
export function emoteKeyLabels(layout: KeyLayout): { readonly before: string; readonly after: string; readonly send: string } {
  return { before: codeLabel(EMOTE_LAYER_KEYS.before, layout), after: codeLabel(EMOTE_LAYER_KEYS.after, layout), send: codeLabel(EMOTE_LAYER_KEYS.send, layout) };
}

/**
 * The actions a fight needs, whose keys the wheel never takes (ADR-114 decision 2.4): the ten ability slots, the
 * stance and Target nearest. Movement is not an action of the table and is never named by the layer.
 */
export const FIGHTING_ACTIONS: readonly ActionId[] = [...Array.from({ length: ABILITY_ACTIONS }, (_, slot) => abilityAction(slot)), "stance", "target"];

/**
 * What the wheel shows of one of its keys: `live` while the layer holds the key and is topmost; `idle` while the
 * layer names it but is not topmost, or the wheel is closed, so a press would be the game's; `none` when the layer
 * does not name the key at all, a fighting action or the wheel's own key holding it.
 */
export type KeyShown = "live" | "idle" | "none";

/**
 * What the wheel shows of its three keys.
 */
export interface LiveKeys {
  readonly before: KeyShown;
  readonly after: KeyShown;
  readonly send: KeyShown;
}

/**
 * Something a view reads the wheel's keys from.
 */
export interface LiveKeysSource {
  readonly subscribe: (listener: () => void) => () => void;
  readonly getSnapshot: () => LiveKeys;
}

/**
 * The bound keys: what the wheel may show of them, and a way to unbind.
 */
export interface EmoteKeys {
  /** What the wheel shows of its three keys now; the same object until it changes. */
  readonly live: LiveKeysSource;
  /** Unbinds the keys and closes the wheel's layer. */
  stop(): void;
}

const TAB = "Tab";

/**
 * What the keys need beside the store and the router.
 */
export interface EmoteKeyOptions {
  /**
   * How many open layers the wheel may open over: the layers of the play windows, which name no key.
   *
   * @returns The count.
   */
  readonly under: () => number;
  /**
   * Whether what has focus is a control that keeps Enter for itself (a button, a link, an option).
   *
   * @param target What had focus when the key was pressed.
   * @returns True for a control.
   */
  readonly isControl: (target: unknown) => boolean;
  /** The key table, whose Emotes key opens and closes the wheel; the page's unless given. */
  readonly keys?: Pick<KeyTable, "code" | "actionOf" | "subscribe">;
}

/**
 * Whether a press is held with a key the browser or the system reads as a shortcut.
 *
 * @param event The press.
 * @returns True with Ctrl, Alt or Meta held.
 */
function shortcut(event: RoutedKey): boolean {
  return event.ctrlKey === true || event.altKey === true || event.metaKey === true;
}

/**
 * Binds the wheel's keys to the page's input router and the wheel's store.
 *
 * @param store The wheel's store, whose open state the layer follows.
 * @param input The page's input router.
 * @param options The layers the wheel may open over, the test for a focused control and the key table.
 * @returns What the wheel may show of its keys, and a way to unbind them.
 * @example
 * const bound = bindEmoteKeys(wheel, input, { under: () => (bag.getSnapshot().open ? 1 : 0), isControl });
 */
export function bindEmoteKeys(store: Pick<EmoteWheelStore, "subscribe" | "getSnapshot" | "toggle" | "close" | "turn" | "send">, input: Pick<InputRouter, "open" | "onKey" | "onLayers" | "depth">, options: EmoteKeyOptions): EmoteKeys {
  const keys = options.keys ?? pageKeys;
  const listeners = new Set<() => void>();
  let layer: LayerHandle | null = null;
  let shown: LiveKeys = { before: "idle", after: "idle", send: "idle" };

  /**
   * Whether the layer names a page key: not while the wheel's own key or a fighting action is bound to it.
   *
   * @param key The page key.
   * @returns True when the layer may take it.
   */
  const named = (key: string): boolean => keys.code("emotes") !== key && !FIGHTING_ACTIONS.some((action) => keys.code(action) === key);

  /**
   * What the wheel shows of a key now.
   *
   * @param isNamed Whether the layer names the key.
   * @returns How it is shown.
   */
  const showing = (isNamed: boolean): KeyShown => (!isNamed ? "none" : layer?.top === true ? "live" : "idle");

  /**
   * Works out what the wheel shows of its keys and tells the listeners when it changed.
   */
  const refresh = (): void => {
    const before = showing(named(EMOTE_LAYER_KEYS.before));
    const after = showing(named(EMOTE_LAYER_KEYS.after));
    const send = showing(true);
    if (before === shown.before && after === shown.after && send === shown.send) {
      return;
    }
    shown = { before, after, send };
    for (const listener of [...listeners]) {
      listener();
    }
  };

  /**
   * The wheel's own key while its layer is topmost: it closes the wheel, with Shift held or not.
   *
   * @param event The press.
   * @returns False for a press that is a shortcut's.
   */
  const ownKey: LayerKeyAnswer = (event) => {
    if (shortcut(event)) {
      return false;
    }
    store.close();
    return true;
  };

  /**
   * A page key's answer: the turn, with Shift held or not, unless the press is a shortcut's.
   *
   * @param step 1 for the page after, -1 for the one before.
   * @returns The answer.
   */
  const turn = (step: 1 | -1): LayerKeyAnswer => (event) => {
    if (shortcut(event)) {
      return false;
    }
    store.turn(step);
    return true;
  };

  /**
   * Enter's answer: sends the selection, with Shift held or not, unless a control has focus or the press is a
   * shortcut's. A press the wheel takes never opens the chat, whether or not the connection took the frame.
   *
   * @param event The press.
   * @returns False for a press left to a control or a shortcut.
   */
  const send: LayerKeyAnswer = (event) => {
    if (shortcut(event) || options.isControl(event.target)) {
      return false;
    }
    store.send();
    return true;
  };

  /**
   * Opens the wheel's layer with the keys it names now: Enter, each page key no fighting action holds, and the
   * wheel's own key, named last so a player who bound it to a page key closes the wheel with it. Bound to Tab the
   * wheel's key is named by no layer, since a layer's keys are answered whatever has focus and Tab must still move
   * focus from a control.
   */
  const openLayer = (): void => {
    const own = keys.code("emotes");
    layer = input.open({
      onClose: () => {
        if (layer !== null) {
          layer = null;
          store.close();
        }
      },
      keys: {
        ...(named(EMOTE_LAYER_KEYS.before) ? { [EMOTE_LAYER_KEYS.before]: turn(-1) } : {}),
        ...(named(EMOTE_LAYER_KEYS.after) ? { [EMOTE_LAYER_KEYS.after]: turn(1) } : {}),
        [EMOTE_LAYER_KEYS.send]: send,
        ...(own === TAB ? {} : { [own]: ownKey }),
      },
    });
  };

  /**
   * Closes the wheel's layer without closing the wheel.
   */
  const dropLayer = (): void => {
    const opened = layer;
    layer = null;
    opened?.close();
  };

  /**
   * Makes the layer follow the store's open state.
   */
  const follow = (): void => {
    const { open } = store.getSnapshot();
    if (open && layer === null) {
      openLayer();
    } else if (!open) {
      dropLayer();
    }
    refresh();
  };

  /**
   * Opens the layer again when the key table changed under an open wheel, so it names the keys in force.
   */
  const rebound = (): void => {
    if (layer !== null) {
      dropLayer();
      openLayer();
    }
    refresh();
  };
  const unsubscribe = store.subscribe(follow);
  const unbind = keys.subscribe(rebound);
  const unwatch = input.onLayers(refresh);
  const unlisten = input.onKey((event) => {
    if (event.repeat || event.defaultPrevented || shortcut(event) || !actionPress(keys, "emotes", event)) {
      return;
    }
    if (store.getSnapshot().open) {
      event.preventDefault();
      store.close();
      return;
    }
    if (input.depth > options.under()) {
      return;
    }
    if (store.toggle()) {
      event.preventDefault();
    }
  });
  follow();
  return {
    live: {
      subscribe: (listener) => {
        listeners.add(listener);
        return () => listeners.delete(listener);
      },
      getSnapshot: () => shown,
    },
    stop: () => {
      unsubscribe();
      unbind();
      unwatch();
      unlisten();
      dropLayer();
      listeners.clear();
    },
  };
}

/**
 * The part of a mouse wheel event the binding reads.
 */
interface WheelTurn {
  readonly deltaY: number;
  readonly deltaMode: number;
  readonly ctrlKey?: boolean;
  readonly target: unknown;
  preventDefault(): void;
}

/**
 * Binds the mouse wheel to the open wheel's pages (ADR-114 decision 2.2 and its note of 2026-10-08, "Turn the page"):
 * one page a notch, down for the page after and up for the one before, wrapping, while the pointer is over the wheel
 * or the world. A turn with Ctrl held is the
 * browser's zoom, which a touchpad's pinch also sends, and is left alone.
 *
 * @param store The wheel's store.
 * @param target Where wheel events are heard: the window.
 * @param over Whether an event's target is the wheel or the world.
 * @returns A function that unbinds it.
 * @example
 * bindEmoteScroll(wheel, window, (target) => target === game.canvas || inWheel(target));
 */
export function bindEmoteScroll(store: Pick<EmoteWheelStore, "getSnapshot" | "scroll">, target: EventTarget, over: (target: unknown) => boolean): () => void {
  /**
   * Takes a wheel event.
   *
   * @param raw The event.
   */
  const onWheel = (raw: Event): void => {
    const event = raw as unknown as WheelTurn;
    if (!store.getSnapshot().open || event.ctrlKey === true || !over(event.target)) {
      return;
    }
    if (store.scroll(wheelDelta(event))) {
      event.preventDefault();
    }
  };
  target.addEventListener("wheel", onWheel, { passive: false });
  return () => target.removeEventListener("wheel", onWheel);
}
